import { Content, ContentNode } from '@amzn/awscat-pattern-library-service-model';

import { getNodeHref } from '../api/ContentUtil';
import { deepCloneContent } from '../model/CloneContent';

/**
 * Business operations on sections of a Pattern.
 *
 * Pattern sections are template-fixed (Summary, Architecture, Epics, etc.) — they
 * are not added, deleted, renamed, or reordered by the author. The operations
 * here cover one-time reconciliation work: filling in sections that older drafts
 * are missing relative to the current template.
 *
 * For Guide section operations (add/delete/rename custom sections within pages)
 * see PageOperations.ts.
 */

/** Thrown by `updateSpecialSection` when the target section is absent. */
export class MissingSectionError extends Error {
  constructor(sectionId: string) {
    super(`Pattern is missing the "${sectionId}" section.`);
    this.name = 'MissingSectionError';
  }
}

/**
 * Section ids on a Pattern whose `contentData` is a structured array
 * rather than a single Tiptap document — they need a custom editor
 * rather than the generic `SectionEditorControl`.
 */
export enum SpecialSectionId {
  Epics = 'epics',
  Troubleshooting = 'troubleshooting',
}

/**
 * Replaces the `contentData` of a structured Pattern section (epics or
 * troubleshooting) with `next`. Returns a new Content; never mutates
 * the input.
 *
 * @throws When the section is absent from the content.
 */
export function updateSpecialSection<T>(
  content: Content,
  sectionId: SpecialSectionId,
  next: readonly T[],
): Content {
  // Clone first — callers always get a fresh Content back.
  const updated: Content = deepCloneContent(content);

  // Find the target section in the cloned tree.
  const section = updated.contentData.contentSections?.find((s) => s.id === sectionId);
  if (!section) {
    throw new MissingSectionError(sectionId);
  }

  // Replace its contents wholesale.
  section.contentData = next as unknown as ContentNode['contentData'];
  return updated;
}

/**
 * Returns the section ids that the template defines but the content is missing,
 * preserving the template's order.
 */
export function findMissingPatternSectionIds(
  content: Content,
  template: Content,
): readonly string[] {
  const existingIds = new Set(content.contentData.contentSections.map((s) => s.id));
  return template.contentData.contentSections
    .filter((templateSection) => !existingIds.has(templateSection.id))
    .map((templateSection) => templateSection.id);
}

/**
 * Returns a new Content with any sections defined by the template but missing
 * from the content inserted at their template-ordered indices. The href of each
 * new section is regenerated from the content's metadata. The original content
 * is not mutated. Returns the same content instance when nothing is missing.
 */
export function addMissingPatternSections(content: Content, template: Content): Content {
  const templateSections = template.contentData.contentSections;
  const existingIds = new Set(content.contentData.contentSections.map((s) => s.id));
  const hasMissing = templateSections.some((s) => !existingIds.has(s.id));
  if (!hasMissing) return content;

  const updated: Content = deepCloneContent(content);
  const sections = updated.contentData.contentSections;
  const existingMap = new Map(sections.map((s) => [s.id, s]));
  const templateIds = new Set(templateSections.map((s) => s.id));

  const result: ContentNode[] = [];
  for (const tSec of templateSections) {
    if (existingMap.has(tSec.id)) {
      result.push(existingMap.get(tSec.id)!);
    } else {
      const newSection: ContentNode = { ...tSec };
      // @ts-expect-error TS2322 - strict migration
      newSection.href = getNodeHref(newSection, updated.contentMetadata);
      result.push(newSection);
    }
  }
  for (const s of sections) {
    if (!templateIds.has(s.id)) result.push(s);
  }
  updated.contentData.contentSections = result;

  return updated;
}
